/**
 * 認証と権限判定の唯一の入口。
 * Firebase Auth のログイン状態と users/{uid} の権限情報をまとめて提供する。
 * 権限判定のロジックはここに集約し、画面側で個別に role を見比べない。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { getDb, getFirebaseAuth } from '@core/lib/firebase';
import {
  flowRoleFromLegacy,
  permissionsForUser,
  type DepartmentId,
  type FlowRole,
  type PermissionKey,
  type Permissions,
  type User,
} from '@core/lib/types';

/** loading: 判定中 / signed-out: 未ログイン / no-profile: users未登録 / inactive: 無効化 / ready: 利用可 */
export type AuthStatus = 'loading' | 'signed-out' | 'no-profile' | 'inactive' | 'ready';

type AuthContextValue = {
  status: AuthStatus;
  user: User | null;
  firebaseUser: FirebaseUser | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** users/{uid} を読み直す(パスワード設定直後など、プロフィールが変わったとき) */
  refreshUser: () => Promise<void>;
  /** 本人がパスワードを設定した直後に呼ぶ。Firestore の印の更新が遅れても、この画面では設定済みとして扱う(2026-09-09) */
  markPasswordSet: () => void;

  // --- 権限判定(firestore.rules と同じ条件をUI側にも実装する) ---
  /** 権限マトリクス。未設定のユーザーはロールから導く */
  permissions: Permissions;
  can: (key: PermissionKey) => boolean;
  /** 承認フロー上の役割(v3.5)。メニューとフロー担当の出し分けに使う */
  flowRole: FlowRole;
  isAdmin: boolean;
  isAccounting: boolean;
  /** 所属している事業部か */
  inDept: (departmentId: DepartmentId) => boolean;
  /** 責任者を務める事業部か */
  isManagerOf: (departmentId: DepartmentId) => boolean;
  /** 承認(→approved)できるか。事業部責任者 または 経理 または 管理者 */
  canApprove: (departmentId: DepartmentId) => boolean;
  /** 確定(→confirmed)できるか。経理 または 管理者のみ */
  canConfirm: boolean;
  /** 承認画面を開けるか(どこかの事業部の責任者、または経理・管理者) */
  canOpenApprovalScreen: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);

  const loadProfile = useCallback(async (fbUser: FirebaseUser) => {
    const snap = await getDoc(doc(getDb(), 'users', fbUser.uid));
    if (!snap.exists()) {
      setUser(null);
      setStatus('no-profile');
      return;
    }
    const data = { id: snap.id, ...snap.data() } as User;
    setUser(data);
    setStatus(data.active ? 'ready' : 'inactive');
  }, []);

  useEffect(() => {
    return onAuthStateChanged(getFirebaseAuth(), async (fbUser) => {
      setFirebaseUser(fbUser);
      if (!fbUser) {
        setUser(null);
        setStatus('signed-out');
        return;
      }
      await loadProfile(fbUser);
    });
  }, [loadProfile]);

  const refreshUser = useCallback(async () => {
    const fb = getFirebaseAuth().currentUser;
    if (fb) await loadProfile(fb);
  }, [loadProfile]);

  const markPasswordSet = useCallback(() => {
    setUser((prev) => (prev ? { ...prev, password_set_at: Timestamp.now() } : prev));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    // 保存先はタブ単位(sessionStorage)。firebase.ts の initializeAuth で固定している
    await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(getFirebaseAuth());
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const isAccounting = user?.is_accounting === true;
    const flowRole: FlowRole =
      user?.flow_role ?? flowRoleFromLegacy(user?.role ?? 'member', isAccounting);
    // 承認者はすべての権限を持つ(2026-09-02 指示)。
    // それ以外は保存済みの権限、未設定なら従来どおりロールから導く
    const permissions: Permissions = permissionsForUser(
      flowRole,
      user?.permissions,
      user?.role ?? 'member',
      isAccounting,
    );
    const can = (key: PermissionKey) => permissions[key] === true;
    // 管理者＝マスタ管理を持つ人(承認者は必ず該当する。2026-09-02 指示)
    const isAdmin = permissions.master_admin === true;
    const inDept = (d: DepartmentId) => user?.department_ids.includes(d) ?? false;
    const isManagerOf = (d: DepartmentId) => user?.manager_department_ids.includes(d) ?? false;
    return {
      status,
      user,
      firebaseUser,
      signIn,
      signOut,
      refreshUser,
      markPasswordSet,
      permissions,
      can,
      flowRole,
      isAdmin,
      isAccounting,
      inDept,
      isManagerOf,
      // 承認は「承認権限があり、その事業部に関われる人」。全社を見られる人は全事業部を承認できる
      canApprove: (d: DepartmentId) =>
        can('approve') && (can('company_pl') || can('confirm') || isManagerOf(d) || inDept(d)),
      canConfirm: can('confirm'),
      canOpenApprovalScreen: can('approve') || can('confirm'),
    };
  }, [status, user, firebaseUser, signIn, signOut, refreshUser, markPasswordSet]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth は AuthProvider の内側で使ってください');
  return ctx;
}

/** ログイン済み前提の画面で使う。未ログイン時は例外にせず null を返さない形にする */
export function useCurrentUser(): User {
  const { user } = useAuth();
  if (!user) throw new Error('ログインユーザーが未取得です(AuthGate の内側で使ってください)');
  return user;
}
